import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCatalog, useConfig } from '../hooks/useApi';
import type { CatalogBanner, CatalogBrand } from '../api/client';
import { ImageFB, BrandGridSkeleton, EmptyState, ErrorState } from '../components';

export function Home({ onContact }: { onContact: () => void }) {
  // 首页与其它页面共用同一份目录快照（见 hooks/useApi 的 useCatalog）
  const { data, loading, error, refetch } = useCatalog();
  const config = useConfig();

  const brands = data?.brands ?? null;
  const banners = data?.banners ?? null;

  return (
    <div>
      <TopBar />

      {/* Carousel */}
      {loading ? (
        <div className="skeleton" style={{ height: 188, borderRadius: 'var(--r-hero)', margin: 'var(--s-3) var(--s-4) var(--s-3)' }} />
      ) : banners && banners.length > 0 ? (
        <Carousel banners={banners} />
      ) : null}

      {config?.promoStripText && (
        <div className="promo-strip">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
            <path d="M6 8.5h12l-1 11a1.5 1.5 0 0 1-1.5 1.3h-7a1.5 1.5 0 0 1-1.5-1.3l-1-11z" />
            <path d="M8.8 8.5V7a3.2 3.2 0 0 1 6.4 0v1.5" />
          </svg>
          <span className="txt">{config.promoStripText}</span>
        </div>
      )}

      {/* Brand Wall */}
      <div className="section-title">
        <span className="ttl">Our Brands</span>
        <span className="en">Wholesale Catalog</span>
        <span className="rule" />
      </div>
      {loading ? (
        <BrandGridSkeleton />
      ) : error ? (
        <ErrorState msg={error} onRetry={refetch} />
      ) : brands && brands.length > 0 ? (
        <div className="brand-grid">
          {brands.map((brand) => (
            <BrandCard key={brand.id} brand={brand} />
          ))}
        </div>
      ) : (
        <EmptyState title="Catalog updating" desc="New brands coming soon" />
      )}

      <div style={{ height: 80 }} />
    </div>
  );
}

function TopBar() {
  return (
    <header className="topbar">
      <div className="brand-label">
        <span className="dot" />
        Bosamie <strong>Myanmar</strong>
      </div>
    </header>
  );
}

function Carousel({ banners }: { banners: CatalogBanner[] }) {
  const [index, setIndex] = useState(0);
  const trackRef = useRef<HTMLDivElement>(null);
  const startX = useRef(0);
  const isDown = useRef(false);

  const next = useCallback(() => {
    setIndex((i) => (i + 1) % banners.length);
  }, [banners.length]);

  useEffect(() => {
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) return;
    const timer = setInterval(next, 3800);
    return () => clearInterval(timer);
  }, [next]);

  const onTouchStart = (e: React.TouchEvent) => {
    startX.current = e.touches[0].clientX;
    isDown.current = true;
  };

  const onTouchEnd = (e: React.TouchEvent) => {
    if (!isDown.current) return;
    isDown.current = false;
    const dx = e.changedTouches[0].clientX - startX.current;
    if (dx > 40) setIndex((i) => (i - 1 + banners.length) % banners.length);
    else if (dx < -40) setIndex((i) => (i + 1) % banners.length);
  };

  return (
    <div
      className="carousel"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <div
        ref={trackRef}
        className="carousel-track"
        style={{ transform: `translateX(-${index * 100}%)` }}
      >
        {banners.map((b) => (
          <div key={b.id} className="carousel-slide">
            <ImageFB src={b.imageUrl} alt="" className="slide-img" />
          </div>
        ))}
      </div>
      <div className="carousel-dots">
        {banners.map((_, i) => (
          <button
            key={i}
            className={`dot ${i === index ? 'active' : ''}`}
            onClick={() => setIndex(i)}
            aria-label={`Slide ${i + 1}`}
          />
        ))}
      </div>
    </div>
  );
}

function BrandCard({ brand }: { brand: CatalogBrand }) {
  const navigate = useNavigate();
  return (
    <div className="brand-card" onClick={() => navigate(`/brand/${brand.id}`)}>
      <div className="brand-cover">
        <ImageFB src={brand.coverUrl} alt={brand.name} />
        <div className="scrim" />
        <div className="bname">{brand.name}</div>
      </div>
      <div className="brand-meta">
        <span className="count">{brand.modelCount} models available</span>
      </div>
    </div>
  );
}
